import type { SessionPreferences, SessionWindow, WaterComfort } from "@/lib/session-planner/types";
import type { ReactNode } from "react";
import type { SessionContext } from "@/lib/session-planner/comfort";
import { windQualityChip } from "@/lib/session-planner/explain";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { prefScoringKey } from "@/lib/session-planner/preferences";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import { SessionSupProfileBeta } from "./SessionPreferencesPanel";

const WIND_CHIP_TONE: Record<"good" | "mixed" | "poor", string> = {
  good: "border-emerald-200 bg-emerald-50 text-emerald-700",
  mixed: "border-amber-200 bg-amber-50 text-amber-700",
  poor: "border-red-200 bg-red-50 text-red-700",
};

const WIND_CHIP_SPORTS = new Set(["surf", "bodyboarding", "wind", "foil", "sailing"]);

const pad = (h: number) => `${String(h).padStart(2, "0")}:00`;

type Props = {
  window: SessionWindow;
  prefs: SessionPreferences;
  ctx: SessionContext;
  waterComfort: WaterComfort;
  actions?: ReactNode;
};

export function BestWindowCard({ window: w, prefs, ctx, waterComfort, actions }: Props) {
  const peakHours = w.hours.filter((h) => h.hour >= w.peakStartHour && h.hour < w.peakEndHour);
  const midSource = peakHours.length ? peakHours : w.hours;
  const mid = midSource[Math.floor(midSource.length / 2)];
  const chip =
    WIND_CHIP_SPORTS.has(prefs.sport)
      ? windQualityChip(mid?.windDirDeg ?? null, ctx.beachFacingDeg ?? null)
      : null;

  const showRideableBand =
    w.peakStartHour !== w.startHour || w.peakEndHour !== w.endHour;

  const waterLine =
    waterComfort.waterTempC !== null
      ? `Water ${Math.round(waterComfort.waterTempC)}°C · ${waterComfort.advice}`
      : waterComfort.advice;
  const namedSupProfile = isSupProfileKey(prefScoringKey(prefs));
  const evidenceNote = "evidenceNote" in w && typeof w.evidenceNote === "string"
    ? w.evidenceNote
    : null;

  return (
    <div className="rounded-[14px] border border-gold/40 bg-gold/[0.08] p-4">
      {/* Eyebrow */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">
          <span aria-hidden>★</span> Best window
        </p>
        {namedSupProfile ? (
          <SessionSupProfileBeta
            scoringKey={prefScoringKey(prefs)}
            surface="Planner"
          />
        ) : null}
      </div>

      {/* Headline row */}
      <div className="mt-0.5 flex items-start justify-between gap-3">
        <p className="font-serif text-[20px] font-bold leading-tight text-navy">
          {w.dayLabel} {pad(w.peakStartHour)}–{pad(w.peakEndHour)}
        </p>
        <span
          className="shrink-0 rounded-full bg-gold px-2.5 py-1 text-[12px] font-bold text-navy"
          aria-label={`Score ${scoreOutOf100(w.peakScore)} out of 100`}
        >
          {scoreOutOf100(w.peakScore)}
        </span>
      </div>

      {/* Rideable band (when peak is tighter than full window) */}
      {showRideableBand ? (
        <p className="mt-0.5 text-[12px] text-text-muted">(rideable {w.timeLabel})</p>
      ) : null}

      {/* Wind direction chip */}
      {chip ? (
        <span
          className={`mt-1.5 inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${WIND_CHIP_TONE[chip.tone]}`}
        >
          {chip.label} wind
        </span>
      ) : null}

      {/* Headline metric line (size · wind-quality · tide) */}
      {w.headline ? (
        <p className="mt-2 text-[13.5px] font-medium text-navy">{w.headline}</p>
      ) : null}

      {evidenceNote ? (
        <p className="mt-1 text-[12px] font-medium text-amber-800">{evidenceNote}</p>
      ) : null}

      {/* Water temperature / wetsuit guide */}
      <p className="mt-1 text-[13px] leading-relaxed text-text-body">{waterLine}</p>

      {/* Warnings */}
      {w.warnings.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {w.warnings.map((warning) => (
            <li
              key={warning}
              className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
            >
              {warning}
            </li>
          ))}
        </ul>
      ) : null}
      {actions}
    </div>
  );
}
